/* eslint-disable @typescript-eslint/no-explicit-any */
import { dxTreeViewItem } from 'devextreme/ui/tree_view';
import { DocumentConfigSearchDisplay } from '../../distribution/models';
import { TransportType } from '../../transport/models/transport-type';
import { AssemblyArtifact, XmlArtifact } from '../models/artifact';
import { ConfigurationKey } from '../models/configuration-key';
import { ConfigurationKeyUsage } from '../models/configuration-key-usage';
import { DocumentTypeDeveloper } from '../models/document-type';
import { ExceptionType } from '../models/exception-type';
import { FileNamingType } from '../models/filenamingtype';
import { Format } from '../models/format';
import { FormatType } from '../models/format-type';
import { ItineraryStep } from '../models/itinerary';
import { InitStep } from '../../distribution/models/init-step';
import { TrackingField } from '../models/tracking-field';
import { Variant } from '../models/variant';
import { VariantVersion } from '../models/variant-version';
import { ItineraryStepDocumentConfigEntity } from '../models/itinerary-step-document-config-entity';
import { XmlPropertyPromotionFilter } from '../models/xml-property-promotion-filter';
import { DuplicateCheckTypeEntity } from '../../tracking/models/duplicate-check-type';
import { DocumentTypeClassifierResolverEntity } from '../models/document-type-classifier-resolver';
import { DocumentTypeClassifier } from '../models/document-type-classifier';
import { ContentTypeGroup } from '../../distribution/models/content-type-group';
import { BatchingConfiguration } from '../models/batchconfiguration';
import { DocumentConfigOutBatcher } from '../models/document-config-out-batcher';
import { ScheduleItem } from '../../shared/scheduling/models';
import { Trigger } from '../../shared/scheduling/models';
import { IdentificationType, IdentificationTypeQualifier } from '../../partner/models';
import { ConcurrencyKey } from '../../configuration/models/concurrency-key';

export interface ExportNode {
    displayName: string;
    treeViewItem: dxTreeViewItem;
    data: any;
    id: string;
    isExplicit: boolean;
    isNew: boolean;
    isExcludable: boolean;
    isExcluded: boolean;
    showExcluded: boolean;
}

export interface DataMapper {
    mapFormatType(id: number): FormatType;
    mapFormat(id: number): Format;
    mapVariant(id: number): Variant;
    mapVersion(id: number): VariantVersion;
    mapDistro(id: number): DocumentTypeDeveloper;
    mapItineraryStep(id: number): ItineraryStep;
    mapInitializeStep(id: number): InitStep;
    mapTransportType(id: number): TransportType;
    mapDocConfig(id: number): DocumentConfigSearchDisplay;
    mapFileNamingType(id: number): FileNamingType;
    mapAssemblyArtifact(id: string): AssemblyArtifact;
    mapXmlArtifact(id: string): XmlArtifact;
    mapDocumentConfigItinerarySteps(DocumentConfigId: number): ItineraryStep[];
    mapDocumentConfigItineraryStepConfigs(DocumentConfigId: number): ItineraryStepDocumentConfigEntity[];
    mapDocumentTypeClassifierResolver(id: number): DocumentTypeClassifierResolverEntity;
    mapContentTypeGroups(id: number): ContentTypeGroup;
    mapBatchingConfigurations(id: number): BatchingConfiguration;
    mapScheduleItems(id: number): ScheduleItem;
    mapScheduleTriggers(id: number): Trigger;
    mapDocumentConfigOutBatchers(id: number): DocumentConfigOutBatcher[];
    mapIdentificationTypes(id: number): IdentificationType[];
    mapConfigurationKeys(dynamicForeignKey: number, keyUsageName: string): ConfigurationKey[];
}

export abstract class ExportNodeBase<T> implements ExportNode {
    isNew = true;
    isExcludable = false;
    isExcluded = false;

    constructor(
        public data: T,
        protected dataMapper: DataMapper,
        public isExplicit: boolean = true) {
        this.initialize();
    }

    // The base class initialize method must always be called, even when overridden. 
    // It is assumed that the data property is never null. 
    // Once reference handling is complete, ensure that the base initialize method is invoked in every overridden implementation.
    initialize(): void {
        if (!this.data) {
            throw new Error('Null data error.');
        }
    }

    get treeViewItem(): dxTreeViewItem {
        let text = this.displayName;
        if (text === null) {
            text = '(null)';
        }
        if (text === '') {
            text = '(blank)';
        }
        return { text, exportNode: this as ExportNode } as dxTreeViewItem;
    }

    get showExcluded(): boolean {
        return this.isExcluded;
    }

    abstract get displayName(): string;
    abstract get id(): string;
}

export class ConcurrencyKeyNode extends ExportNodeBase<ConcurrencyKey> {

    override initialize(): void {
    }

    get displayName(): string {
        return this.data.Name;
    }

    get id(): string {
        return this.data.Id.toString();
    }
}

export class FormatTypeNode extends ExportNodeBase<FormatType> {
    initStepNode: InitializeStepNode;
    probeArtifact: AssemblyArtifactNode;
    configurationKeyNodes: ConfigKeyNode[];

    // The use of optional chaining (data?) is a temporary workaround to handle cases where data might be null or undefined. 
    // Once proper reference handling is implemented, this workaround should be removed to ensure stricter data integrity.
    override initialize(): void {
        if (this.data?.DisassemblerInitStepId > 0) {
            this.initStepNode = new InitializeStepNode(this.dataMapper.mapInitializeStep(this.data.DisassemblerInitStepId), this.dataMapper, false);
        }
        
        if (this.data?.ProbeArtifactId?.length) {
            this.probeArtifact = new AssemblyArtifactNode(this.dataMapper.mapAssemblyArtifact(this.data.ProbeArtifactId), this.dataMapper, false);
        }

        this.configurationKeyNodes = this.dataMapper.mapConfigurationKeys(this.data.Id,'Distribution_FormatType')?.map(k => new ConfigKeyNode(k, this.dataMapper, [], false)) ?? [];
    }

    get displayName(): string {
        return this.data.Name;
    }

    get id(): string {
        return this.data.Id.toString();
    }
}

export class FormatNode extends ExportNodeBase<Format> {
    formatTypeNode: FormatTypeNode;

    override initialize(): void {
        this.formatTypeNode = new FormatTypeNode(this.dataMapper.mapFormatType(this.data.FormatTypesId), this.dataMapper, false);
    }

    get displayName(): string {
        return this.data.FormatName;
    }

    get id(): string {
        return this.data.FormatsId.toString();
    }
}

export class VariantNode extends ExportNodeBase<Variant> {
    get displayName(): string {
        return this.data.VariantName;
    }

    get id(): string {
        return this.data.VariantsId.toString();
    }
}

export class VersionNode extends ExportNodeBase<VariantVersion> {
    get displayName(): string {
        return this.data.VariantVersionName;
    }

    get id(): string {
        return this.data.VariantVersionsId.toString();
    }
}

export class ItineraryStepNode extends ExportNodeBase<ItineraryStep> {
    assemblyArtifact: AssemblyArtifactNode;

    override initialize(): void {
        this.assemblyArtifact = new AssemblyArtifactNode(this.dataMapper.mapAssemblyArtifact(this.data.ArtifactId), this.dataMapper, false);
    }

    get displayName(): string {
        return this.data.Name;
    }

    get id(): string {
        return this.data.Id.toString();
    }
}

export class InitializeStepNode extends ExportNodeBase<InitStep> {
    assemblyArtifact: AssemblyArtifactNode;

    override initialize(): void {
        this.assemblyArtifact = new AssemblyArtifactNode(this.dataMapper.mapAssemblyArtifact(this.data.ArtifactId), this.dataMapper, false);
    }

    get displayName(): string {
        return `${this.data.Name} / ${this.data.StepKey}`;
    }

    get id(): string {
        return this.data.Id.toString();
    }
}

export class TransportTypeNode extends ExportNodeBase<TransportType> {
    assemblyArtifact: AssemblyArtifactNode;

    override initialize(): void {
        this.assemblyArtifact = new AssemblyArtifactNode(this.dataMapper.mapAssemblyArtifact(this.data.ArtifactId), this.dataMapper, false);
    }

    get displayName(): string {
        return `${this.data.Name} / ${this.data.TransportType} / ${this.data.TransportKey}`;
    }

    get id(): string {
        return this.data.Id.toString();
    }
}

export class DuplicateCheckNode extends ExportNodeBase<DuplicateCheckTypeEntity> {
    assemblyArtifact: AssemblyArtifactNode;

    override initialize(): void {
        this.assemblyArtifact = new AssemblyArtifactNode(this.dataMapper.mapAssemblyArtifact(this.data.ArtifactId), this.dataMapper, false);
    }

    get displayName(): string {
        return `${this.data.Name} /  ${this.data.DuplicateCheckKey}`;
    }

    get id(): string {
        return this.data.Id.toString();
    }
}

export class DocumentConfigOutBatcherNode extends ExportNodeBase<DocumentConfigOutBatcher> {
    assemblyArtifact: AssemblyArtifactNode;
    //docConfigNode: DocConfigNode;

    override initialize(): void {
        this.assemblyArtifact = new AssemblyArtifactNode(this.dataMapper.mapAssemblyArtifact(this.data.ArtifactId), this.dataMapper, false);
        //this.docConfigNode = new DocConfigNode(this.dataMapper.mapDocConfig(this.data.DocumentConfigId), this.dataMapper, false);
    }

    get displayName(): string {
        return `${this.data.Id}`;
    }

    get id(): string {
        return this.data.Id.toString();
    }
}

export class DocumentTypeClassifierResolverNode extends ExportNodeBase<DocumentTypeClassifierResolverEntity> {
    assemblyArtifact: AssemblyArtifactNode;
    contentTypeGroupNode: ContentTypeGroupNode;

    override initialize(): void {
        this.assemblyArtifact = new AssemblyArtifactNode(this.dataMapper.mapAssemblyArtifact(this.data.ArtifactId), this.dataMapper, false);
        this.contentTypeGroupNode = new ContentTypeGroupNode(this.dataMapper.mapContentTypeGroups(this.data.ContentTypeGroupId), this.dataMapper, false);
    }

    get displayName(): string {
        return `${this.data.Name}`;
    }

    get id(): string {
        return this.data.Id.toString();
    }
}


export class DocumentTypeClassifierNode extends ExportNodeBase<DocumentTypeClassifier> {
    resolvernode: DocumentTypeClassifierResolverNode;
    distroNode: DistroNode;
    // idTypeNode: IdTypeNode;
    docConfigNode: DocConfigNode;
    controlArtifact: AssemblyArtifactNode;

    override initialize(): void {
        if (this.data.DocumentTypeClassifierResolverId !== null)
            this.resolvernode = new DocumentTypeClassifierResolverNode(this.dataMapper.mapDocumentTypeClassifierResolver(this.data.DocumentTypeClassifierResolverId), this.dataMapper, false);
        if (this.data.DocumentTypeId !== null)
            this.distroNode = new DistroNode(this.dataMapper.mapDistro(this.data.DocumentTypeId), this.dataMapper, false);
        // if (this.data.IdentificationTypeId !== null)
        //     this.distroNode = new IdTypeNode(this.dataMapper.mapIdType(this.data.IdentificationTypeId), this.dataMapper, false);
        if (this.data.DocumentConfigId !== null)
            this.docConfigNode = new DocConfigNode(this.dataMapper.mapDocConfig(this.data.DocumentConfigId), this.dataMapper, false);
        if (this.data.ControlMessageInResolverArtifactId !== null) {
            this.controlArtifact = new AssemblyArtifactNode(this.dataMapper.mapAssemblyArtifact(this.data.ControlMessageInResolverArtifactId), this.dataMapper, false);
        }
    }

    get displayName(): string {
        return `${this.data.Name}`;
    }

    get id(): string {
        return this.data.Id.toString();
    }
}



export class ContentTypeGroupNode extends ExportNodeBase<ContentTypeGroup> {
    get displayName(): string {
        return `${this.data.Name}`;
    }

    get id(): string {
        return this.data.Id.toString();
    }
}


export class DistroNode extends ExportNodeBase<DocumentTypeDeveloper> {
    fileNamingTypeNodes: FileNamingTypeNode[];
    CdmSchemaArtifact: XmlArtifactNode;
    CdmStylesheetArtifact: XmlArtifactNode;
    configurationKeyNodes: ConfigKeyNode[];

    // The use of optional chaining (data?) is a temporary workaround to handle cases where data might be null or undefined. 
    // Once proper reference handling is implemented, this workaround should be removed to ensure stricter data integrity.
    override initialize(): void {
        this.fileNamingTypeNodes = this.data?.FilenamingTypes.map(e => new FileNamingTypeNode(e, this.dataMapper, false));

        if (this.data?.CdmStylesheetArtifactId?.length) {
            this.CdmStylesheetArtifact = new XmlArtifactNode(this.dataMapper.mapXmlArtifact(this.data.CdmStylesheetArtifactId), this.dataMapper, false);
        }

        this.configurationKeyNodes = this.dataMapper.mapConfigurationKeys(this.data.Id,'Distribution_DistributionObject')?.map(k => new ConfigKeyNode(k, this.dataMapper, [], false)) ?? [];
    }

    get displayName(): string {
        return this.data.Name;
    }

    get id(): string {
        return this.data.Id.toString();
    }
}

export class DocConfigNode extends ExportNodeBase<DocumentConfigSearchDisplay> {
    formatNode: FormatNode;
    variantNode: VariantNode;
    versionNode: VersionNode;
    distroNode: DistroNode;
    itineraryStepNodes: ItineraryStepNode[];
    documentConfigOutBatchers: DocumentConfigOutBatcherNode[];
    configurationKeyNodes: ConfigKeyNode[];

    override initialize(): void {
        this.distroNode = new DistroNode(this.dataMapper.mapDistro(this.data.DistributionObjectId), this.dataMapper, false);
        this.formatNode = new FormatNode(this.dataMapper.mapFormat(this.data.FormatId), this.dataMapper, false);
        this.variantNode = new VariantNode(this.dataMapper.mapVariant(this.data.VariantId), this.dataMapper, false);
        this.versionNode = new VersionNode(this.dataMapper.mapVersion(this.data.VariantVersionsId), this.dataMapper, false);
        this.itineraryStepNodes = this.dataMapper.mapDocumentConfigItinerarySteps(this.data.DocumentConfigId).map(isn => new ItineraryStepNode(isn, this.dataMapper, false));
        this.documentConfigOutBatchers = this.dataMapper.mapDocumentConfigOutBatchers(this.data.DocumentConfigId).map(config => new DocumentConfigOutBatcherNode(config, this.dataMapper, false));
        this.configurationKeyNodes = this.dataMapper.mapConfigurationKeys(this.data.DocumentConfigId,'Distribution_DocumentConfig')?.map(k => new ConfigKeyNode(k, this.dataMapper, [], false)) ?? [];
    }

    get displayName(): string {
        return `${this.distroNode.displayName} / ${this.data.Direction} / ${this.formatNode.displayName} / ${this.variantNode.displayName} / ${this.versionNode.displayName}`;
    }

    get id(): string {
        return this.data.DocumentConfigId.toString();
    }
}

export class TrackingFieldNode extends ExportNodeBase<TrackingField> {
    distroNode: DistroNode;

    override initialize(): void {
        if (this.data.DistributionObjectsId && this.data.DistributionObjectsId > 0)
            this.distroNode = new DistroNode(this.dataMapper.mapDistro(this.data.DistributionObjectsId), this.dataMapper, false);
    }

    get displayName(): string {
        if (this.distroNode)
            return `${this.distroNode.displayName} / ${this.data.Name}`;
        return this.data.Name;
    }

    get id(): string {
        return this.data.TrackingFieldTypesId.toString();
    }
}

export class BatchingConfigurationNode extends ExportNodeBase<BatchingConfiguration> {
    distroNode: DistroNode;
    scheduleNode: ScheduleItemNode;

    override initialize(): void {
        if (this.data.DocumentType && this.data.DocumentType.Id > 0 )
            this.distroNode = new DistroNode(this.dataMapper.mapDistro(this.data.DocumentType.Id), this.dataMapper, false);
        this.scheduleNode = new ScheduleItemNode(this.dataMapper.mapScheduleItems(this.data.ScheduleItem.Id), this.dataMapper, false);
    }

    get displayName(): string {
        if (this.distroNode) {
            return `${this.distroNode.displayName} / ${this.data.Name}`;
        }
        return this.data.Name;
    }

    get id(): string {
        return this.data.Id.toString();
    }
}

export class ScheduleItemNode extends ExportNodeBase<ScheduleItem> {
    triggers: ScheduleTriggerNode[];

    override initialize(): void {
        if (this.data.Triggers?.length)
            this.triggers = this.data.Triggers.map(e => new ScheduleTriggerNode(e, this.dataMapper, false));
    }

    get displayName(): string {

        return this.data.Id.toString();
    }

    get id(): string {
        return this.data.Id.toString();
    }
}

export class ScheduleTriggerNode extends ExportNodeBase<Trigger> {


    get displayName(): string {

        return this.data.Id.toString();
    }

    get id(): string {
        return this.data.Id.toString();
    }
}

export class IdentificationTypeNode extends ExportNodeBase<IdentificationType> {
    qualifiers: IdentificationTypeQualifierNode[];

    override initialize(): void {
        if (this.data.Qualifiers?.length)
            this.qualifiers = this.data.Qualifiers.map(e => new IdentificationTypeQualifierNode(e, this.dataMapper, false));
    }

    get displayName(): string {

        return this.data.Name.toString();
    }

    get id(): string {
        return this.data.IdentificationTypeId.toString();
    }
}

export class IdentificationTypeQualifierNode extends ExportNodeBase<IdentificationTypeQualifier> {


    get displayName(): string {

        return this.data.Qualifier.toString();
    }

    get id(): string {
        return this.data.IdentificationTypeQualifiersId.toString();
    }
}


export class ConfigKeyNode extends ExportNodeBase<ConfigurationKey> {
    distroNode: DistroNode;
    formatTypeNode: FormatTypeNode;
    docConfigNode: DocConfigNode;
    usage: ConfigurationKeyUsage[] = [];
    
    constructor(
        data: ConfigurationKey,
        dataMapper: DataMapper,
        usage: ConfigurationKeyUsage[],
        isExplicit: boolean = true) {
        super(data, dataMapper, isExplicit );
        this.usage = usage;
        this.initialize();
    }

    override initialize(): void {
        if (!this.usage?.length) return;

        const keyname = this.usage.find(u => u.KeyUsageId === this.data.ConfigurationKeyUsagesId).KeyUsageName;
        if (keyname === 'Distribution_DistributionObject') {
            if (this.data.DynamicForeignKey === null) {
                throw new Error(`The configuration key "${this.data.KeyName}" must have an association defined with a document type, but this association is currently missing. Please establish the association and retry the operation.`);
            }
            this.distroNode = new DistroNode(this.dataMapper.mapDistro(this.data.DynamicForeignKey), this.dataMapper, false);
        }
        if (keyname === 'Distribution_FormatType') {
            if (this.data.DynamicForeignKey === null) {
                throw new Error(`The configuration key "${this.data.KeyName}" must have an association defined with a format type, but this association is currently missing. Please establish the association and retry the operation.`);
            }
            this.formatTypeNode = new FormatTypeNode(this.dataMapper.mapFormatType(this.data.DynamicForeignKey), this.dataMapper, false);
        }
        if (keyname === 'Distribution_DocumentConfig') {
            if (this.data.DynamicForeignKey === null) {
                throw new Error(`The configuration key "${this.data.KeyName}" must have an association defined with a document configuration, but this association is currently missing. Please establish the association and retry the operation.`);
            }
            this.docConfigNode = new DocConfigNode(this.dataMapper.mapDocConfig(this.data.DynamicForeignKey), this.dataMapper, false);
        }
    }

    get displayName(): string {
        return this.data.KeyName;
    }

    get id(): string {
        return this.data.ConfigurationKeysId.toString();
    }
}

export class ExceptionTypeNode extends ExportNodeBase<ExceptionType> {
    get displayName(): string {
        return `${this.data.ErrorCode}: ${this.data.TechnicalDescription}`;
    }

    get id(): string {
        return this.data.ExceptionTypesId.toString();
    }
}

export class FileNamingTypeNode extends ExportNodeBase<FileNamingType> {
    get displayName(): string {
        return `${this.data.FilenamingTypeName}`;
    }

    get id(): string {
        return this.data.FilenamingTypesId.toString();
    }
}

export class XmlArtifactNode extends ExportNodeBase<XmlArtifact> {
    override initialize(): void {
        this.isExcludable = true;
    }
    get displayName(): string {
        return `${this.data.XmlType} / ${this.data.Name}`;
    }

    get id(): string {
        return this.data.ArtifactId; //TODO: We do not have an ID to return on artifacts, this framework is working on these ids to be able to get things from the DB on the export time.
    }
}

export class AssemblyArtifactNode extends ExportNodeBase<AssemblyArtifact> {
    override initialize(): void {

    }
    get displayName(): string {
        return `${this.data.AssemblyName} / ${this.data.ClassName} / ${this.data.Name}`;
    }

    get id(): string {
        return this.data.ArtifactId; //TODO: We do not have an ID to return on artifacts, this framework is working on these ids to be able to get things from the DB on the export time.
    }
}

export class XmlPropertyPromotionFilterNode extends ExportNodeBase<XmlPropertyPromotionFilter> {
    get displayName(): string {
        return `${this.data.Description} / ${this.data.RootNode} / ${this.data.RootNamespace}`;
    }

    get id(): string {
        return this.data.Id.toString();
    }
}
